import React from "react";

import {
	faChevronLeft,
	faChevronRight,
	faSortAmountDown,
	faSortAmountUp,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { flexRender, type Table } from "@tanstack/react-table";
import cx from "classnames";

import styles from "./DataGrid.module.css";

function EmptyState({ message, colSpan }: { message: string; colSpan: number }) {
	return (
		<tr>
			<td colSpan={colSpan} className={styles.emptyStateCell}>
				{message}
			</td>
		</tr>
	);
}

interface DataGridProps<TData> {
	table: Table<TData>;
	onNextPage?: () => void;
	hasNextPage?: boolean;
	onPreviousPage?: () => void;
	hasPreviousPage?: boolean;
	emptyStateMessage: string;
	isRefetching?: boolean;
}

export function DataGrid<TData>({
	table,
	onNextPage,
	hasNextPage,
	onPreviousPage,
	hasPreviousPage,
	emptyStateMessage,
	isRefetching = false,
}: DataGridProps<TData>) {
	const isEmpty = table.getRowCount() === 0;

	return (
		<div className={styles.tableContainer}>
			<div className={styles.tableScrollContainer}>
				<table className={styles.table}>
					<thead>
						{table.getHeaderGroups().map(headerGroup => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map(header => {
									const { column } = header;
									const isSortable = column.getCanSort();
									return (
										<th
											key={header.id}
											className={cx(styles.tableHeader, { [styles.pinnedCell]: column.getIsPinned() })}
											onClick={column.getToggleSortingHandler()}
										>
											<div className={cx(styles.columnHeader, { [styles.sortable]: isSortable })}>
												<span>{flexRender(column.columnDef.header, header.getContext())}</span>
												{isSortable && (
													<>
														{{
															asc: <FontAwesomeIcon className={styles.icon} icon={faSortAmountUp} />,
															desc: <FontAwesomeIcon className={styles.icon} icon={faSortAmountDown} />,
														}[column.getIsSorted() as string] ?? null}
													</>
												)}
											</div>
										</th>
									);
								})}
							</tr>
						))}
					</thead>
					<tbody>
						{isEmpty ? (
							<EmptyState colSpan={table.getAllColumns().length} message={emptyStateMessage} />
						) : (
							table.getRowModel().rows.map(row => (
								<tr key={row.id} className={styles.tableRow}>
									{row.getVisibleCells().map(cell => {
										const { column } = cell;
										return (
											<td key={cell.id} className={cx(styles.tableCell, { [styles.pinnedCell]: column.getIsPinned() })}>
												{flexRender(column.columnDef.cell, cell.getContext())}
											</td>
										);
									})}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{!isEmpty && (
				<div className={styles.tableFooter}>
					<button className={styles.pageButton} onClick={onPreviousPage} disabled={!hasPreviousPage || isRefetching}>
						<FontAwesomeIcon icon={faChevronLeft} />
					</button>
					<p>
						{table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
					</p>
					<button className={styles.pageButton} onClick={onNextPage} disabled={!hasNextPage || isRefetching}>
						<FontAwesomeIcon icon={faChevronRight} />
					</button>
				</div>
			)}
		</div>
	);
}
